import { ENTITY_TYPES_MAPPING, DIAG_TYPES } from '../utils/constants';
import logExternalContentRead from '../utils/logExternalContentRead';
import { useRouter, useRoute, Router } from 'vue-router';
import {
  Entity,
  useAdminStore,
  useFavoritesStore,
  useKnowledgeStore,
  ExternalContent,
} from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import useExternalRedirection from './useExternalRedirection';
import useNotification from './useNotification';
import { useHelpdeskStore } from '../stores/helpdesk';

type EntityAction = {
  entityId: string;
  entityType: string;
  entityAncestors: string | null;
  knowledgeId: string | undefined;
  lang: string;
};

const useEntities = (argRouter?: Router) => {
  const router = argRouter || useRouter();
  const route = useRoute();
  const configStore = useConfigStore();

  const getConfig = () => {
    const adminStore = useAdminStore();
    const agentStore = useAgentStore();
    const {
      subdomainUrl,
      company,
      hasAnswerCopyPasteShortcut,
      hasArticleCopyPasteShortcut,
      hasModalClosureShortcut,
      salesForceFocusTab,
      manuallyTriggerActions,
      displayFullTitle,
      displayArticleInMainFrame,
      contentFontSize,
      hasKeepFiltersDuringNavigation,
      hasKeepSearchInputDuringNavigation,
      hasCompanyNoAutomaticActionPreference,
      hasCompanyBrainAgentsPreference,
    } = storeToRefs(adminStore);

    const { forceMainFrame, hostApp, positionIsLeft, positionIsTop } =
      storeToRefs(configStore);
    const { availableAgents, agentHasScopeSelect } = storeToRefs(agentStore);
    const hasGenius =
      !!availableAgents.value.length && hasCompanyBrainAgentsPreference.value;

    return {
      subdomainUrl:
        subdomainUrl.value || (import.meta.env.VITE_APP_FRONT_URL as string),
      companyPreferences: company.value.preferences,
      hasAnswerCopyPasteShortcut: hasAnswerCopyPasteShortcut.value,
      hasArticleCopyPasteShortcut: hasArticleCopyPasteShortcut.value,
      hasModalClosureShortcut: hasModalClosureShortcut.value,
      salesForceFocusTab: salesForceFocusTab.value,
      manuallyTriggerActions: manuallyTriggerActions.value,
      displayFullTitle: displayFullTitle.value,
      displayArticleInMainFrame:
        forceMainFrame.value || displayArticleInMainFrame.value,
      contentFontSize: contentFontSize.value,
      hostApp: hostApp.value,
      positionIsLeft: positionIsLeft.value,
      positionIsTop: positionIsTop.value,
      hasKeepFiltersDuringNavigation: hasKeepFiltersDuringNavigation.value,
      hasKeepSearchInputDuringNavigation:
        hasKeepSearchInputDuringNavigation?.value,
      hasCompanyNoAutomaticActionPreference:
        hasCompanyNoAutomaticActionPreference?.value,
      hasGenius,
      displayGeniusTrigger: agentHasScopeSelect.value && hasGenius,
      isMobile: true,
      canInjectAnswer: true,
    };
  };

  const showEntity = ({
    id,
    type,
    source,
    lang,
  }: {
    id: string;
    type: string;
    source?: string;
    lang?: string;
  }) => {
    if (type === 'ExternalContent') return;
    if (type === 'Case')
      return goToEntity({
        id,
        type,
        deepLoading: true,
        force: true,
        source,
        lang,
      });
    router.push({
      query: { modalContentId: id, source, force: 1, lang },
    });
  };

  const goToEntity = (
    content: GoToEntityType | (ExternalContent & { type: string }),
  ) => {
    let { type } = content;
    if (type === 'ExternalContent') {
      return logExternalContentRead(
        content as ExternalContent,
        'chrome-extension',
        configStore.$logs,
      );
    }
    let {
      id,
      force = false,
      skipOrdered = false,
      deepLoading = false,
      forceModal = false,
      showBack = false,
      fromModal = false,
      fromToast = false,
      hash = '',
      lang = '',
      source,
      notifyLanguageSwitch,
      highlights,
      queryText,
      skipWorkflowActions = false,
    } = content as GoToEntityType;
    if (!ENTITY_TYPES_MAPPING[type]) return;

    const knowledgeStore = useKnowledgeStore();
    const adminStore = useAdminStore();

    const { knowledgeId, mainContent, path, modalContent, selectedLanguage } =
      storeToRefs(knowledgeStore);
    const { displayArticleInMainFrame } = storeToRefs(adminStore);
    const { forceMainFrame, hostApp } = storeToRefs(configStore);

    source = source || 'navigation';
    lang = lang || selectedLanguage.value;

    // if type is Answer, entity come from modal so we want to display back button
    if (type === 'Answer')
      return router.push({
        query: { modalAnswerId: id, modalBack: fromModal ? 1 : 0 },
      });

    let modalBack = false;
    // Check if back button should be display on modal
    if (fromModal && showBack) modalBack = true;

    // Check if back button should be display on main body
    let mainBack = false;
    if (showBack) {
      mainBack = isDiag(mainContent.value.type) && isDiag(type);
    }

    let modalContentId;
    if (
      type === 'Article' &&
      !displayArticleInMainFrame.value &&
      !forceMainFrame.value
    ) {
      // When clicking on an article : open modal with article
      modalContentId = id + hash;
      id =
        path.value.length > 1
          ? mainContent.value.id
          : (knowledgeId.value as string);
      type = path.value.length > 1 ? mainContent.value.type : 'Knowledge';
      hash = mainContent.value.hash || '';
    } else if (forceModal && showBack) {
      modalContentId = id + hash;
      id = mainContent.value.id;
      type = mainContent.value.type;
    } else if (forceModal && fromToast) {
      modalContentId = id;
      id = knowledgeId.value!;
      type = 'Knowledge';
    } else if (forceModal && !fromModal) {
      // When we force the opening of a content in the modal
      // Set the content of the main body to the previous collection and force the deeploading so that the breadcrumb is updated for the main content
      modalContentId = mainContent.value.id + hash;

      const collection = path.value.at(-2);
      id = collection?.id;
      type = 'Case';
      deepLoading = true;
      hash = '';
    } else if (fromModal && showBack && type !== 'Article') {
      // When a diagnosis is clicked from the modal
      // The content of the modal is kept and the diagnosis is opened in the main body with a deeploading to generate the breadcrumb
      modalContentId = modalContent.value.id;
      deepLoading = true;
    } else if (
      route &&
      !!route.query.modalContentId &&
      (route.query.modalContentId as string).split('#')[0] !== id &&
      (route.query.modalContentId as string).split('#')[1] &&
      (type !== 'Knowledge' || hash)
    ) {
      // If we focus on a hash of the main content or
      // if we navigate inside a collection we want to keep the modal open
      modalContentId = route.query.modalContentId;
    } else if (fromModal) {
      // If click on content come from modal we keep content in modal
      return router.push({
        query: {
          modalContentId: id + hash,
          modalBack: modalBack ? 1 : 0,
          lang,
          source,
        },
      });
    }

    const newRoute = {
      path: `/${lang}/${ENTITY_TYPES_MAPPING[type]}/${id}`,
      query: {
        force: force ? 1 : 0,
        skipOrdered: skipOrdered ? 1 : 0,
        deepLoading: deepLoading ? 1 : 0,
        modalContentId,
        modalBack: modalBack ? 1 : 0,
        mainBack: mainBack ? 1 : 0,
        fromToast: fromToast ? 1 : 0,
        source,
        notifyLanguageSwitch,
        skipWorkflowActions: skipWorkflowActions ? 1 : 0,
        ...(highlights && highlights.length > 0
          ? { highlights: JSON.stringify(highlights), queryText }
          : {}),
      },
      hash,
    } as EntityRoute;
    router.push(newRoute);
  };

  const goToLastEntity = () => {
    const { lastEntityRoute } = storeToRefs(configStore);
    if (!lastEntityRoute.value)
      return router.push({
        path: '/',
        query: { force: '0' },
      });

    const { query } = lastEntityRoute.value;

    router.push({
      ...lastEntityRoute.value,
      query: { ...query, noSave: '1' },
    });
  };

  const forceModal = () => {
    const routeQueryText = route.query?.queryText;
    const routeQueryHighlights = route.query?.highlights as string;
    const knowledgeStore = useKnowledgeStore();
    const { mainContent } = storeToRefs(knowledgeStore);
    const entity = {
      id: mainContent.value.id,
      type: mainContent.value.type,
      deepLoading: true,
      forceModal: true,
      hash: mainContent.value.hash,
      ...(routeQueryHighlights && routeQueryHighlights.length > 0
        ? { highlights: JSON.parse(routeQueryHighlights) }
        : {}),
      ...(routeQueryText && routeQueryText.length > 0
        ? { queryText: routeQueryText }
        : {}),
    };
    goToEntity(entity);
  };

  const closeModal = () => {
    const { hostApp } = storeToRefs(configStore);
    switch (hostApp.value) {
      case 'zendesk':
        const helpdeskStore = useHelpdeskStore();
        helpdeskStore.closeModal();
        break;
      default:
        router.push({ query: {} });
        break;
    }
  };

  const hasMainBackButton = computed(() => {
    return route.query.mainBack === '1';
  });

  const hasModalBackButton = computed(() => {
    return route.query.modalBack === '1';
  });

  const isDiag = (type: string) => {
    return DIAG_TYPES.includes(type);
  };

  const goBack = () => {
    const { previousEntityRoute } = storeToRefs(configStore);
    const { resetPath } = useKnowledgeStore();

    if (previousEntityRoute.value) {
      const { query } = previousEntityRoute.value;
      router.push({
        ...previousEntityRoute.value,
        query: { ...query, deepLoading: 1 },
      });
    } else {
      resetPath();
      router.push({ path: '/' });
    }

    configStore.flushLastTwoRoutes();
  };

  const toggleFavorite = (contentId: string): void => {
    const favoritesStore = useFavoritesStore();
    const { selectedLanguage } = useKnowledgeStore();

    const { notify } = useNotification();
    const isFavorite = !!favoritesStore.itemIsFavorite(
      contentId,
      selectedLanguage,
    );
    if (isFavorite) {
      favoritesStore.removeFavoriteContent(contentId, selectedLanguage);
    } else {
      favoritesStore.addUserFavorite(contentId, selectedLanguage);
    }

    const key = isFavorite ? 'remove-favorite' : 'add-favorite';
    notify(`knowledge.notification.${key}`);
  };

  const favoriteIds = computed(() => {
    const { favoriteIds } = useFavoritesStore();
    return favoriteIds;
  });

  const copyLink = ({
    entityId,
    entityType,
    knowledgeId,
    lang,
  }: EntityAction): void => {
    const { webUrl, entityUrl } = useExternalRedirection();
    const { notify } = useNotification();
    navigator.clipboard.writeText(
      webUrl(
        entityUrl({
          entityId,
          entityType,
          knowledgeId,
          lang,
        }),
      ),
    );

    notify(`knowledge.notification.copy-link`);
  };

  const openInWeb = ({
    entityId,
    entityType,
    knowledgeId,
    lang,
  }: EntityAction) => {
    const { selectedLanguage } = useKnowledgeStore();
    const { redirectUrl, entityUrl } = useExternalRedirection();
    const url = redirectUrl(
      entityUrl({
        entityId,
        entityType,
        knowledgeId,
        lang: lang || selectedLanguage,
      }),
    );

    if (!url) return;
    window.open(url, '_blank');
  };

  const goToFeedback = ({
    modalContentId,
    source,
    lang,
  }: {
    modalContentId: string;
    source: string;
    lang: string;
  }) => {
    router.push({
      path: '/feedbacks',
      query: {
        modalContentId,
        source,
        lang,
        new: 1,
      },
    });
  };

  const modalIsOpen = computed(
    () =>
      (!!route.query.modalAnswerId && route.query.modalAnswerId !== '-1') ||
      (!!route.query.modalContentId && route.query.modalContentId !== '-1'),
  );

  const changeLanguage = (lang: string) => {
    const query = route.query;

    /**
     * Since we are copying query, we make sure that the focused tab does not:
     * - send silent query otherwise middleware will just early return
     * - send noSave query otherwise lastEntityRouteHandler doesn't register language switch route
     */
    delete query['silent'];
    delete query['noSave'];
    query.source = 'language_switch';
    router.push({
      params: {
        lang,
      },
      query: {
        ...query,
        lang,
      },
    });
  };

  const openGenius = (trigger: GeniusTrigger, scope: GeniusScope) => {
    configStore.openGenius(trigger, scope);
    router.push({ name: 'copilot' });
  };

  return {
    showEntity,
    goToEntity,
    goToLastEntity,
    toggleFavorite,
    favoriteIds,
    getConfig,
    copyLink,
    openInWeb,
    goToFeedback,
    forceModal,
    closeModal,
    hasMainBackButton,
    hasModalBackButton,
    goBack,
    modalIsOpen,
    changeLanguage,
    openGenius,
  };
};

export default useEntities;
